import React from 'react'
import { useEffect, useState } from "react";
import "../styles/About.css"
import { skillsData } from "../script/aboutSkillsdata"
import { teamData } from "../script/aboutTeamdata"
import { timelineData } from "../script/aboutTimelineData"
import { Helmet } from "react-helmet";

const INTRO_FEATURES = [
  { tint: 'purple', title: 'Verified skill, not a certificate', desc: '30+ metrics prove what you can actually build.' },
  { tint: 'pink', title: 'Real production challenges', desc: 'System design and debugging, not textbook drills.' },
  { tint: 'green', title: 'A clear path to your next role', desc: 'AI roadmap to your first ₹20L+ engineering job.' },
];

const HIRING_SIGNALS = [
  { label: 'Logic Velocity', desc: 'Speed under pressure' },
  { label: 'Error Resilience', desc: 'Debugging & optimization' },
  { label: '2026 Readiness', desc: 'Match to today\u2019s hiring bar' },
];

// Unique partner logos — duplicated later in the JSX for the seamless
// scrolling marquee effect. Deduplicated here so we don't repeat markup.
const PARTNER_LOGOS = [
  "company-3.png",
  "company-6.png",
  "company-5.png",
  "company-2.png",
  "company-4.png",
  "company-1.png",
];

export default function About() {

  const [animate, setAnimate] = useState(false);



  useEffect(() => {
    setTimeout(() => setAnimate(true), 300);
  }, []);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) =>
        prev === timelineData.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (

    <div className="cma-root">

      <Helmet>
        <title>CodeMatrix - AI-Powered Engineering Skill Intelligence Platform</title>
        <meta name="description" content="CodeMatrix measures real-world engineering capability using AI-powered coding simulations, skill verification & hiring metrics." />

        {/* Speed up the very first image below (it's above the fold) */}
        <link rel="preload" as="image" href="/aboutBuilding1.jpeg" />

        {/* Open the connection to the logo CDN early so those requests
            don't pay the full DNS+TLS handshake cost when they're needed */}
        <link rel="preconnect" href="https://demosites.royal-elementor-addons.com" crossOrigin="" />
        <link rel="dns-prefetch" href="https://demosites.royal-elementor-addons.com" />
      </Helmet>

      {/* ---------------------Intro Section--------------------- */}
      <section className="cma-section cma-intro">
        <div className="cma-container cma-intro__grid">

          <div className="cma-intro__content">
            <span className="cma-eyebrow">★ Who We Are</span>
            <h2>The bridge between learning and earning</h2>

            <p className="cma-intro__lede">
              CodeMatrix is a Skill Intelligence Platform — data-driven proof of engineering
              capability that hiring teams trust more than a resume.
            </p>

            <div className="cma-feature-list">
              {INTRO_FEATURES.map((f) => (
                <div className="cma-feature" key={f.title}>
                  <span className={`cma-feature__icon cma-tint--${f.tint}`}>✓</span>
                  <div className="cma-feature__body">
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="cma-signal-strip">
              {HIRING_SIGNALS.map((s) => (
                <div className="cma-signal" key={s.label}>
                  <span className="cma-signal__label">{s.label}</span>
                  <span className="cma-signal__desc">{s.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cma-intro__image">
            <img
              src="aboutBuilding1.jpeg"
              alt="Developer working at CodeMatrix"
              width="800"
              height="600"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ---------------------Skills Section --------------------- */}
      <section className="cma-section cma-section--soft cma-skills">
        <div className="cma-container cma-skills__grid">

          <div className="cma-skills__content">
            <span className="cma-eyebrow">★ Our Expertise</span>
            <h2>Engineering DNA, measured</h2>
            <p>Live stress-testing and architectural simulations, not static grades.</p>

            <div className="cma-skill-bars">
              {skillsData.map((skill, i) => (
                <div className="cma-skill" key={i}>
                  <div className="cma-skill__label">
                    <span>{skill.name}</span>
                    <span>{skill.value}%</span>
                  </div>

                  <div className="cma-skill__track">
                    <div
                      className={`cma-skill__fill ${skill.className || ''}`}
                      style={{ width: animate ? `${skill.value}%` : "0%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="cma-skills__image">
            <img
              src="aboutEngineering1.jpeg"
              alt="Engineers collaborating"
              width="800"
              height="600"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ----------------Team Section---------------- */}

      <section className="cma-section cma-team">
        <div className="cma-container">

          <div className="cma-heading-block">
            <span className="cma-eyebrow">★ The People</span>
            <h2>Our team</h2>
            <p>Engineers and mentors building future-ready developers.</p>
          </div>

          <div className="cma-team__row">
            {teamData.map((member, index) => (
              <div className="cma-team__card" key={index}>

                <div className="cma-team__avatar">
                  <img
                    src={member.img}
                    alt={member.name}
                    width="160"
                    height="160"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <h3>{member.name}</h3>
                <span className="cma-team__role">{member.role}</span>

                <p>{member.desc}</p>

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* -------------------Journey Section------------------------- */}

      <section className="cma-section cma-section--soft cma-journey">
        <div className="cma-container cma-journey__grid">

          <div className="cma-journey__image">
            <img
              src="aboutJourney.jpeg"
              alt="CodeMatrix Journey"
              width="800"
              height="600"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="cma-journey__content">

            <span className="cma-eyebrow">★ Company Timeline</span>
            <h2>Our growth trajectory</h2>

            <div className="cma-timeline">

              <button
                type="button"
                className="cma-timeline__arrow"
                aria-label="Previous phase"
                disabled={index === 0}
                onClick={() => setIndex((prev) => prev - 1)}
              >
                ‹
              </button>

              <div className="cma-timeline__track">
                {timelineData.map((item, i) => (
                  <button
                    type="button"
                    key={i}
                    className={`cma-timeline__step ${i === index ? "cma-is-active" : ""}`}
                    onClick={() => setIndex(i)}
                  >
                    <span className="cma-timeline__dot" />
                    <span>{item.phase}</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="cma-timeline__arrow"
                aria-label="Next phase"
                disabled={index === timelineData.length - 1}
                onClick={() => setIndex((prev) => prev + 1)}
              >
                ›
              </button>
            </div>

            <div className="cma-year-cards">

              <div className="cma-year-card">
                <h4>{timelineData[index]?.title}</h4>
                <p>{timelineData[index]?.desc}</p>
              </div>

              <div className="cma-year-card">
                <h4>{timelineData[index]?.title2}</h4>
                <p>{timelineData[index]?.desc2}</p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* --------------------------Partners Section----------------- */}
      <section className="cma-section cma-partners">
        <div className="cma-container">

          <div className="cma-heading-block cma-heading-block--tight">
            <span className="cma-eyebrow">★ Technology Partners</span>
            <h2>Trusted by companies building the future</h2>
          </div>

          <div className="cma-logo-wrapper">
            <div className="cma-logo-track">

              {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((img, index) => (
                <div className="cma-logo-item" key={index}>
                  <img
                    src={`https://demosites.royal-elementor-addons.com/online-course-v2/wp-content/uploads/sites/165/2025/07/${img}`}
                    alt="partner"
                    width="120"
                    height="60"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}

            </div>
          </div>

        </div>
      </section>

    </div>
  )
}